import pino from 'pino';
import type { Logger as PinoLogger } from 'pino';

/**
 * Creates a wrapper function that supports both argument orders:
 * - logger.info(obj, msg) - Pino's native format
 * - logger.info(msg, obj) - Alternative format
 * - logger.info(msg) - Message only
 */
function createLogWrapper(
  pinoLogger: PinoLogger,
  level: 'info' | 'error' | 'warn' | 'debug' | 'fatal',
) {
  return function (first: unknown, second?: unknown, ...args: unknown[]) {
    // If first arg is a string and second is an object, swap them for Pino
    if (
      typeof first === 'string' &&
      second !== undefined &&
      typeof second === 'object' &&
      second !== null &&
      !Array.isArray(second)
    ) {
      // Pattern: logger.info("message", {obj})
      pinoLogger[level](second, first, ...args);
    } else if (typeof first === 'string' && second === undefined) {
      // Pattern: logger.info("message")
      (pinoLogger[level] as (msg: string) => void)(first);
    } else if (typeof first === 'string' && typeof second === 'string') {
      // Pattern: logger.info("message", "extra") - treat second string as part of message
      (pinoLogger[level] as (msg: string, ...args: unknown[]) => void)(
        first,
        second,
        ...args,
      );
    } else {
      // Pattern: logger.info({obj}, "message") - Pino's native format
      pinoLogger[level](first, second as string | undefined, ...args);
    }
  };
}

/**
 * @name Logger
 * @description A logger implementation using Pino with support for both argument orders
 */
const pinoInstance = pino({
  browser: {
    asObject: true,
  },
  level: 'debug',
  base: {
    env: process.env.NODE_ENV,
  },
  errorKey: 'error',
});

const Logger = {
  info: createLogWrapper(pinoInstance, 'info'),
  error: createLogWrapper(pinoInstance, 'error'),
  warn: createLogWrapper(pinoInstance, 'warn'),
  debug: createLogWrapper(pinoInstance, 'debug'),
  fatal: createLogWrapper(pinoInstance, 'fatal'),
};

export { Logger };
